import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getDJRecords, updateSongRequestStatus, getDJInfo, subscribeToDJRequests } from '../api/supabase';
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  useMediaQuery,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import FilterListIcon from '@mui/icons-material/FilterList';
import SongCard from '../components/DJView/SongCard';
import LogoExtendido from '../assets/Logo DJ Ponla extendido (sin fondo).png';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import { motion, AnimatePresence } from 'framer-motion';

const DJView = () => {
  const { djId } = useParams();
  const [songs, setSongs] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [loadingSongs, setLoadingSongs] = useState({});
  const [error, setError] = useState(null);
  const [djInfo, setDjInfo] = useState({});
  const [currentMessage, setCurrentMessage] = useState(0);
  const [loadingDJInfo, setLoadingDJInfo] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [processedSongIds, setProcessedSongIds] = useState(() => {
    // Recuperar IDs procesados de localStorage al iniciar
    const savedIds = localStorage.getItem(`${djId}_processedSongs`);
    return savedIds ? new Set(JSON.parse(savedIds)) : new Set();
  });
  const [newSongNotifications, setNewSongNotifications] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const messages = [
    '🎧 Recuerda marcar las canciones que ya tocaste 😎',
    '⬅️ Desliza a la izquierda las canciones que no te interesen 🎶',
    '😎 Aún no hay canciones, pero pronto tendrás una lista increíble',
    '💪 Recuerda, tú controlas la música. ¡Hazlo épico!',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessage((prevMessage) => (prevMessage + 1) % messages.length);
    }, 5000); // Cambia cada 5 segundos

    return () => clearInterval(interval);
  }, [messages.length]);

  useEffect(() => {
    // Solicitar permiso para notificaciones
    const requestNotificationPermission = async () => {
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        // Guardar preferencia del usuario
        localStorage.setItem('notificationPermission', permission);
      }
    };

    if (!localStorage.getItem('notificationPermission')) {
      requestNotificationPermission();
    }
  }, []);

  // Reemplazar la función showNotification actual
  const showNotification = useCallback((songName, artist) => {
    if (Notification.permission === 'granted' && document.visibilityState !== 'visible') {
      // Evitar notificaciones duplicadas verificando si ya se ha mostrado
      const notificationId = `${songName}-${artist}`;
      if (!sessionStorage.getItem(notificationId)) {
        new Notification('Nueva solicitud de canción', {
          body: `${songName} - ${artist}`,
          icon: '/LogoDJP.png'
        });
        // Marcar esta notificación como mostrada por esta sesión
        sessionStorage.setItem(notificationId, 'true');
        // Limpiar después de 30 segundos para permitir notificar de nuevo si llega otra igual más tarde
        setTimeout(() => sessionStorage.removeItem(notificationId), 30000);
      }
    }
  }, []);

  useEffect(() => {
    // Función para limpiar canciones procesadas antiguas
    const cleanupProcessedSongs = () => {
      try {
        // Obtener datos almacenados
        const savedIdsStr = localStorage.getItem(`${djId}_processedSongs`);
        if (!savedIdsStr) return;

        const savedIds = JSON.parse(savedIdsStr);
        const savedIdsWithDates = localStorage.getItem(`${djId}_processedSongsDates`);
        const idsWithDates = savedIdsWithDates ? JSON.parse(savedIdsWithDates) : {};

        const now = new Date();
        const twoWeeksAgo = new Date(now.setDate(now.getDate() - 14)); // Mantener 2 semanas de datos

        // Filtrar IDs antiguos
        const recentIds = savedIds.filter(id => {
          const timestamp = idsWithDates[id];
          if (!timestamp) return true; // Mantener IDs sin timestamp
          return new Date(timestamp) >= twoWeeksAgo;
        });

        // Filtrar timestamps correspondientes
        const updatedIdsWithDates = {};
        recentIds.forEach(id => {
          if (idsWithDates[id]) {
            updatedIdsWithDates[id] = idsWithDates[id];
          }
        });

        // Guardar datos actualizados
        localStorage.setItem(`${djId}_processedSongs`, JSON.stringify(recentIds));
        localStorage.setItem(`${djId}_processedSongsDates`, JSON.stringify(updatedIdsWithDates));

      } catch (error) {
        console.error('Error durante limpieza de canciones procesadas:', error);
      }
    };

    // Ejecutar limpieza al montar el componente
    cleanupProcessedSongs();

    // Configurar intervalo de limpieza diaria
    const cleanupInterval = setInterval(cleanupProcessedSongs, 24 * 60 * 60 * 1000);

    // Limpiar intervalo al desmontar componente
    return () => clearInterval(cleanupInterval);
  }, [djId]);

  const fetchSongs = useCallback(async () => {
    try {
      const records = await getDJRecords(djId);
      const sortedRecords = records.sort(
        (a, b) => new Date(b.fields['Created']) - new Date(a.fields['Created'])
      );
      setSongs(sortedRecords);
    } catch (error) {
      console.error('Error fetchSongs:', error);
    }
  }, [djId]);

  // Carga inicial
  useEffect(() => {
    fetchSongs();
  }, [fetchSongs]);

  // Configurar suscripción Realtime - INDEPENDIENTE de fetchSongs y isDeleting
  useEffect(() => {
    let activeSubscription = null;
    let isMounted = true;

    const setupSubscription = async () => {
      const subscription = await subscribeToDJRequests(
        djId,
        (newSong) => {
          // Callback para INSERT
          setSongs(prevSongs => {
            // Verificar si ya existe para evitar duplicados
            if (prevSongs.some(s => s.id === newSong.id)) return prevSongs;

            // Agregar nueva canción al principio
            const updatedSongs = [newSong, ...prevSongs];

            // Notificar
            showNotification(newSong.fields['Song Name'], newSong.fields['Artist']);

            return updatedSongs;
          });
        },
        (updatedSong) => {
          // Callback para UPDATE
          // Si el estado cambió a algo diferente de 'pendiente', la quitamos
          if (updatedSong.fields['Option'] && updatedSong.fields['Option'] !== 'pendiente') {
            setSongs(prevSongs => prevSongs.filter(s => s.id !== updatedSong.id));
          }
        }
      );

      if (!isMounted) {
        // Si el componente se desmontó mientras esperábamos, limpiar inmediatamente
        if (subscription) {
          subscription.unsubscribe();
        }
        return;
      }

      activeSubscription = subscription;
    };

    setupSubscription();

    return () => {
      isMounted = false;
      if (activeSubscription) {
        activeSubscription.unsubscribe();
      }
    };
  }, [djId, showNotification]); // Eliminada dependencia de isDeleting

  // Eliminar notificaciones después de 5 segundos
  useEffect(() => {
    if (newSongNotifications.length > 0) {
      const timer = setTimeout(() => {
        setNewSongNotifications(prev =>
          prev.filter(notification => Date.now() - notification.timestamp < 5000)
        );
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [newSongNotifications]);

  useEffect(() => {
    const fetchDJInfo = async () => {
      try {
        setLoadingDJInfo(true);
        const info = await getDJInfo(djId);
        setDjInfo(info);
      } catch (error) {
        console.error('Error fetching DJ info:', error);
      } finally {
        setLoadingDJInfo(false);
      }
    };
    fetchDJInfo();
  }, [djId]);

  const handleDelete = async (songKey, option = '') => {
    // Declarar la variable fuera del bloque try para que esté disponible en todo el ámbito
    let songsToRestoreIfError = [];

    try {
      // Prevenir múltiples clicks
      if (loadingSongs[songKey]) {
        return;
      }

      // Bloquear la recarga automática durante la eliminación
      setIsDeleting(true);

      // Encontrar todas las canciones que coinciden
      const songsToDelete = songs.filter((song) => {
        const key = `${song.fields['Song Name']} - ${song.fields['Artist']}`;
        return key === songKey;
      });

      if (songsToDelete.length === 0) {
        return;
      }

      // Guardar una copia de las canciones a eliminar para posible restauración
      songsToRestoreIfError = [...songsToDelete];

      // Activar loading por songKey
      setLoadingSongs(prev => ({ ...prev, [songKey]: true }));

      // Actualizar UI inmediatamente para mejor experiencia
      setSongs(prevSongs => prevSongs.filter(song => {
        const key = `${song.fields['Song Name']} - ${song.fields['Artist']}`;
        return key !== songKey;
      }));

      // En Supabase usamos el mismo ID (form_id) para identificar al DJ
      // const newTableId = djId.replace(/^f|m$/g, '');

      // Procesar todas las eliminaciones en paralelo con mejor gestión de errores
      let hasError = false;
      await Promise.all(songsToDelete.map(async (song) => {
        try {
          // Actualizar el estado de la canción en lugar de moverla y borrarla
          await updateSongRequestStatus(song.id, option);
        } catch (error) {
          console.error(`Error al actualizar canción: ${song.id}`, error);
          hasError = true;
          throw error;
        }
      }));
    } catch (error) {
      console.error('Error en handleDelete:', error);
      // Revertir cambios solo con las canciones afectadas
      setSongs(prevSongs => {
        return [...prevSongs, ...songsToRestoreIfError];
      });
    } finally {
      // Limpiar estados
      setLoadingSongs(prev => ({ ...prev, [songKey]: false }));
      // Permitir que la recarga automática continúe
      setIsDeleting(false);
    }
  };

  const handleFilter = useCallback(() => {
    setIsFiltered((prev) => !prev);
  }, []);

  const groupedSongs = useMemo(() => {
    const songMap = {};
    songs.forEach((song) => {
      const key = `${song.fields['Song Name']} - ${song.fields['Artist']}`;
      if (!songMap[key]) {
        songMap[key] = { ...song, count: 0 };
      }
      songMap[key].count += 1;
    });

    return Object.values(songMap).sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count; // Ordena por número de repeticiones
      }
      return new Date(b.fields['Created']) - new Date(a.fields['Created']); // Ordena por fecha de llegada
    });
  }, [songs]);

  const filteredSongs = useMemo(() => {
    const songsToFilter = isFiltered ? [...groupedSongs].sort((a, b) =>
      a.fields['Artist'].localeCompare(b.fields['Artist'])
    ) : groupedSongs;
    return songsToFilter;
  }, [groupedSongs, isFiltered]);


  // Función para determinar si una canción es nueva (agregada en los últimos 5 minutos)
  const isNewSong = (createdDate) => {
    const now = new Date();
    const songDate = new Date(createdDate);
    const diffInMinutes = (now - songDate) / (1000 * 60);
    return diffInMinutes <= 5; // Puedes ajustar el tiempo según tus necesidades
  };


  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#121212', color: '#fff', py: 2 }}>
      <Container maxWidth="md">
        {/* Mostrar notificaciones flotantes con colores de la marca */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 20, // Cambiado a bottom para mejor visibilidad 
            right: 20,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxHeight: '60vh',
            overflowY: 'auto',
            msOverflowStyle: 'none', /* para IE y Edge */
            scrollbarWidth: 'none', /* para Firefox */
            '&::-webkit-scrollbar': { display: 'none' }, /* para Chrome */
          }}
        >
          {newSongNotifications.map(notification => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: 50, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100 }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 20,
                duration: 0.5
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: 'rgba(20, 20, 20, 0.85)',
                  backdropFilter: 'blur(12px)',
                  color: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                  maxWidth: 320,
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  position: 'relative',
                  overflow: 'hidden',
                  background: 'linear-gradient(145deg, rgba(30,30,30,0.9) 0%, rgba(20,20,20,0.95) 100%)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: 'linear-gradient(to bottom, #d50000, #ff1744)',
                    boxShadow: '0 0 10px rgba(213, 0, 0, 0.5)'
                  }
                }}
                onClick={() => {
                  // Eliminar esta notificación al hacer clic
                  setNewSongNotifications(prev =>
                    prev.filter(item => item.id !== notification.id)
                  );
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      p: 0.5
                    }}
                  >
                    <img
                      src="/LogoDJP.png"
                      alt="DJ Ponla Logo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 800,
                        color: '#fff',
                        mb: 0.5,
                        fontSize: '0.95rem',
                        letterSpacing: '0.3px'
                      }}
                      noWrap
                    >
                      ¡Nueva solicitud! 🎉
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: '100%',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        fontWeight: 600,
                        color: 'rgba(255, 255, 255, 0.95)',
                        fontSize: '0.9rem'
                      }}
                    >
                      {notification.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        maxWidth: '100%',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.8rem'
                      }}
                    >
                      {notification.artist}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', marginBottom: 2 }}>
          <img
            src={LogoExtendido}
            alt='Logo DJPonla'
            style={{ width: '60%', height: 'auto', borderRadius: 10 }}
          />
        </Box>

        {loadingDJInfo ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress color="secondary" />
          </Box>
        ) : !djInfo['Cuenta Activa'] ? (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              my: 4,
              backgroundColor: 'rgba(255, 0, 0, 0.1)',
              borderRadius: 2,
              border: '1px solid #ff5252'
            }}
          >
            <Typography
              variant="h5"
              align="center"
              sx={{
                color: '#ff5252',
                fontWeight: 'bold',
                mb: 2
              }}
            >
              Acceso Restringido
            </Typography>
            <Typography
              variant="body1"
              align="center"
              sx={{ color: '#ffffff' }}
            >
              Lo sentimos, esta vista está temporalmente desactivada hasta que se renueve la suscripción.
            </Typography>
          </Paper>
        ) : (
          <>
            {/* Mensaje de Bienvenida Mejorado */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: isMobile ? '1.5rem' : '2rem',
                  fontWeight: 'bold',
                  mb: 1,
                }}
              >
                ¡Bienvenido, {djInfo.Name}!
              </Typography>
            </Box>

            {/* Aquí añadimos el texto con el total de canciones */}
            <Box
              sx={{
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography
                variant="h5"
                align="center"
                gutterBottom
                sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem', mr: 1 }}
              >
                La gente quiere escuchar
              </Typography>
            </Box>

            {/* Botón de filtro y contador de canciones */}
            <Box
              sx={{
                textAlign: 'center',
                mb: 4,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                startIcon={<FilterListIcon />}
                sx={{ borderRadius: 5, fontSize: isMobile ? '0.8rem' : '1rem' }}
                onClick={handleFilter}
              >
                {isFiltered ? 'Mostrar todo' : 'Filtrar por Artista'}
              </Button>
              {/* Mostramos el ícono y el número de canciones al lado derecho del botón */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <LibraryMusicIcon sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
                <Typography variant="h6" sx={{ fontSize: isMobile ? '1rem' : '1.2rem' }}>
                  {filteredSongs.length}
                </Typography>
              </Box>
            </Box>

            {/* Mensaje dinámico para animar al DJ cuando no hay canciones */}
            {filteredSongs.length === 0 && (
              <Box
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  color: '#fff',
                  marginTop: 4,
                }}
              >
                <motion.div
                  key={currentMessage}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  style={{ marginTop: '1rem' }}
                >
                  <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                    {messages[currentMessage]}
                  </Typography>
                </motion.div>
              </Box>
            )}

            {/* Lista de canciones */}
            <motion.div layout>
              <Grid container spacing={2} component={motion.div} layout>
                <AnimatePresence mode="popLayout">
                  {filteredSongs.map((song) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      key={song.id} // Usar ID único en lugar de index para animaciones correctas
                      component={motion.div}
                      layout
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
                      transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    >
                      <SongCard
                        song={song}
                        handleDelete={handleDelete}
                        loading={loadingSongs[`${song.fields['Song Name']} - ${song.fields['Artist']}`]}
                        isNew={isNewSong(song.fields['Created'])}
                        showTableNumber={djId === 'fm0924tom'}
                      />
                    </Grid>
                  ))}
                </AnimatePresence>
              </Grid>
            </motion.div>

            {error && (
              <Box textAlign="center" mt={4}>
                <Typography variant="h6" color="error">
                  {error}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default DJView;
